/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { setReviewMediaGalleryOpen } from "@Pages/PropertyPage/data/propertyPageReducer";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import Portal from "@Components/Portal";
import Modal from "@Components/Modal";
import useIsDesktop from "hooks/useIsDesktop";
import { useHistory } from "hooks/useHistory";
import RatingOverview from "./components/RatingOverview";
import ReviewCardsList from "./components/ReviewCardsList";
import ReviewsPanel from "./components/ReviewsPanel";
import { buildReviewsData } from "./buildReviewsData";
import { trackParameterDrawerExpanded, trackReviewLinkLanded } from "./analytics";
import { reviewUrlParams, updateReviewUrlParams } from "./utils";
import {
  parseReviewParams,
  type ParsedReviewParams,
} from "@Pages/PropertyPage/data/reviewDeepLink";
import styles from "./reviewsSectionV2.module.scss";
import ReviewMediaGallery from "./components/ReviewMediaGallery";

const DEEP_LINK_SETTLE_MS = 50;

const ReviewsSectionV2: React.FC<any> = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [scrollToReviewId, setScrollToReviewId] = useState<string | null>(null);
  // @ts-ignore
  const [galleryIndex, setGalleryIndex] = useState(0);
  /** The gallery's open item, reported up so this component can put it in the URL. */
  const [activeMedia, setActiveMedia] = useState<{
    reviewId: string;
    mediaNumber: number;
  } | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedView = useRef(false);
  const { t } = useTranslationHook();
  const isDesktop = useIsDesktop();
  const history = useHistory();
  const dispatch = useDispatch();

  // Listen for external "open reviews panel" event (e.g. from star rating click)
  useEffect(() => {
    const handleOpenPanel = () => setIsPanelOpen(true);
    const handleClosePanel = () => {
      setIsPanelOpen(false);
      setScrollToReviewId(null);
    };
    window.addEventListener("open-reviews-panel", handleOpenPanel);
    window.addEventListener("close-reviews-panel", handleClosePanel);
    return () => {
      window.removeEventListener("open-reviews-panel", handleOpenPanel);
      window.removeEventListener("close-reviews-panel", handleClosePanel);
    };
  }, []);

  const {
    reviewsRating,
    reviewSummary,
    rawReviews,
    propertyName,
    propertyId,
    cityId,
    isGalleryOpen,
  } = useSelector(
    ({ propertyPageData }: AppState) => ({
      reviewsRating: propertyPageData?.data?.reviews_rating,
      reviewSummary: propertyPageData?.data?.meta?.review_summary,
      rawReviews: propertyPageData?.inventoryReviewData,
      propertyName: propertyPageData?.data?.name || propertyPageData?.data?.region?.name || "",
      propertyId: propertyPageData?.data?.id || "",
      cityId: propertyPageData?.data?.region_id || propertyPageData?.data?.region?.id || "",
      isGalleryOpen: propertyPageData?.review_media_gallery_open || false,
    }),
    shallowEqual,
  );
  const {
    overallRating,
    ratingLabel,
    reviewCount,
    categories,
    summary,
    photos,
    reviews,
    mediaArray,
    mediaIndexMap,
  } = useMemo(
    () => buildReviewsData({ reviewsRating, reviewSummary, rawReviews, propertyName }),
    [reviewsRating, reviewSummary, rawReviews, propertyName],
  );

  // Deep-link grammar lives in @Pages/PropertyPage/data/reviewDeepLink. The params stay in the
  // URL until the user closes the views, so a remount can re-read them.
  const hasHandledDeepLink = useRef(false);
  /** "shared_url" only while the gallery opened from the URL is still the open one. */
  const galleryEntrySource = useRef<"click" | "shared_url">("click");
  // The section remounts during load; a `true` left by the previous instance must not render
  // a gallery at the wrong index.
  const ownsGallery = useRef(false);
  // A shared link naming a review opens that media item alone, with no drawer behind it, so one
  // Back lands on the plain property page. The URL keeps `reviews=all` either way — only the
  // drawer is suppressed. Set by the load-time deep link only; an in-page click clears it.
  const sharedMediaLanding = useRef(false);
  useEffect(
    () => () => {
      dispatch(setReviewMediaGalleryOpen(false));
    },
    [],
  );
  /**
   * Open exactly the views the URL describes, close the rest. Shared by the load-time deep link
   * and Back/Forward so the two cannot disagree. Never tracks — a Back is not a landing.
   */
  const applyUrlToViews = useCallback(
    (override?: ParsedReviewParams) => {
      const { openReviews, reviewId, mediaNumber } =
        override ?? parseReviewParams(window.location.search);
      // API review ids are numbers; the URL param is a string
      const review = reviewId ? reviews.find((r) => String(r.id) === reviewId) : undefined;
      // `photos` holds the review's whole media strip - images and videos - which is what
      // `review_media` numbers.
      const mediaCount = review?.photos.length || 0;
      const mediaStart = review ? mediaIndexMap.get(review.id) : undefined;
      // FR-13/14/16: an unknown review, one from another property, one with no media, or a
      // property with no reviews all land on the reviews view with no error.
      const canOpenGallery = !!review && !!mediaCount && mediaStart !== undefined;

      const mediaOnly = canOpenGallery && sharedMediaLanding.current;
      setIsPanelOpen(openReviews && !mediaOnly);
      setScrollToReviewId(openReviews && !mediaOnly && review ? review.id : null);

      if (canOpenGallery) {
        // FR-15: missing, non-numeric, below 1 or above the media count all mean the first item.
        const n = Number(mediaNumber);
        const offset = Number.isInteger(n) && n >= 1 && n <= mediaCount ? n - 1 : 0;
        setGalleryIndex((mediaStart as number) + offset);
        galleryEntrySource.current = "shared_url";
        ownsGallery.current = true;
      }
      if (!canOpenGallery) setActiveMedia(null);
      dispatch(setReviewMediaGalleryOpen(canOpenGallery));

      return {
        openReviews,
        isFallback: !!reviewId && !canOpenGallery,
        mediaOpened: canOpenGallery,
      };
    },
    [reviews, mediaIndexMap],
  );

  useEffect(() => {
    if (typeof window === "undefined" || hasHandledDeepLink.current) return undefined;
    const params = parseReviewParams(window.location.search);
    if (!params.openReviews) return undefined;
    if (params.reviewId && !reviews.length) return undefined; // retry once reviews are in the store

    // ponytail: fixed settle delay. The section remounts during load, and opening before that
    // races useModal's push/goBack. Swap for a "page settled" signal if one ever exists.
    const timer = setTimeout(() => {
      hasHandledDeepLink.current = true;
      sharedMediaLanding.current = !!params.reviewId;
      // Clear first: useModal stamps each entry with the URL as it stands at push time, so
      // otherwise every entry inherits the deepest view's URL and closing the gallery lands on
      // an entry that still describes it. `params` was read above, so the views still open.
      updateReviewUrlParams(reviewUrlParams({ open: false }));
      const { isFallback, mediaOpened } = applyUrlToViews(params);
      trackReviewLinkLanded({
        propertyId,
        device: isDesktop ? "desktop" : "mobile",
        fallback: isFallback,
        // What actually opened. An id that resolves to nothing falls back to the drawer, so it
        // reports "drawer"; `fallback` still marks it as a media link that missed.
        linkType: mediaOpened ? "media" : "drawer",
      });
    }, DEEP_LINK_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [reviews, mediaIndexMap, propertyId, isDesktop, applyUrlToViews]);

  /**
   * Back and Forward. `useModal` only ever closes, so Forward moved the URL and left the page
   * as it was; reconciling against the URL covers both directions. The zero timeout defers past
   * useModal's own popstate handlers, which would close a view we just reopened.
   */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let timer: ReturnType<typeof setTimeout>;
    const syncFromUrl = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // The URL is in charge from here on, so the load-time deep link must not fire later
        // and reopen a view the user has just navigated away from.
        hasHandledDeepLink.current = true;
        applyUrlToViews();
      }, 0);
    };
    window.addEventListener("popstate", syncFromUrl);
    return () => {
      window.removeEventListener("popstate", syncFromUrl);
      clearTimeout(timer);
    };
  }, [applyUrlToViews]);

  // Mirror the drawer/gallery state in the URL. This section is the ONLY writer of review
  // params, because React runs parent effects last: by the time this runs, the drawer's and the
  // gallery's `useModal` pushes have both happened, so each write lands on the entry that view
  // owns. The gallery used to write its own params from a child effect, which ran first and so
  // stamped them onto the *drawer's* entry - closing the gallery then went "back" onto an entry
  // that still described it, and it reopened.
  //
  // Only a real close clears it: unmount effects don't re-run this, so the load-time remount
  // above leaves a pending deep link intact.
  const hasOpenedReviews = useRef(false);
  useEffect(() => {
    if (isPanelOpen || isGalleryOpen) {
      hasOpenedReviews.current = true;
      // `mediaNumber: undefined` leaves the media params alone — blanking them mid-open wipes
      // what a Forward is about to be reconciled against.
      updateReviewUrlParams(
        reviewUrlParams({
          open: true,
          galleryOpen: isGalleryOpen,
          reviewId: activeMedia?.reviewId,
          mediaNumber: activeMedia?.mediaNumber,
        }),
      );
      return;
    }
    if (!hasOpenedReviews.current) return;
    // Nothing open, so this entry describes no review view. Unconditional beats predicting
    // whether a goBack is pending: one that does follow lands on an already-correct URL.
    updateReviewUrlParams(reviewUrlParams({ open: false }));
  }, [isPanelOpen, isGalleryOpen, activeMedia]);

  // Track review_section_viewed when >= 50% visible
  useEffect(() => {
    if (typeof window === "undefined" || !sectionRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true;
          // trackReviewSectionViewed({
          //   propertyId,
          //   cityId,
          //   totalReviewCount: reviewCount,
          //   aiSummaryRendered: Boolean(summary),
          // });
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [propertyId, cityId, reviewCount, summary]);

  // Track parameter_drawer_expanded when reviews panel is opened
  useEffect(() => {
    if (isPanelOpen && propertyId) {
      trackParameterDrawerExpanded({
        propertyId,
        initialVisibleParameters: categories.map((c) => c.label),
      });
    }
  }, [isPanelOpen]);

  const handleMediaClick = (index: number) => {
    sharedMediaLanding.current = false;
    setGalleryIndex(index);
    galleryEntrySource.current = "click";
    ownsGallery.current = true;
    // The gallery reports its item on mount; until then the previous open's must not be written.
    setActiveMedia(null);
    dispatch(setReviewMediaGalleryOpen(true));
  };

  const handleReadMore = (reviewId: string) => {
    setScrollToReviewId(reviewId);
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
    setScrollToReviewId(null);
  };

  // Notify SectionNavigation to re-scan DOM when this section mounts/unmounts
  useEffect(() => {
    if (typeof window === "undefined") return;
    const event = new Event("navigation_section_removed");
    window.dispatchEvent(event);
  }, [reviewCount, summary]);

  if (reviewCount === 0 && !summary) return null;

  return (
    <section className={styles.container} id="reviews" ref={sectionRef}>
      <div className={styles.header}>
        <h3 className={styles.headerTitle}>{t("propertypage.reviews.title")}</h3>
      </div>

      <div className={styles.content}>
        <RatingOverview
          overallRating={overallRating}
          ratingLabel={ratingLabel}
          reviewCount={reviewCount}
          categories={categories}
          summary={summary}
          photos={photos}
          onMediaClick={handleMediaClick}
          propertyId={propertyId}
        />
      </div>

      {reviews.length > 0 && (
        <div className={styles.reviewCardsSection}>
          <ReviewCardsList
            reviews={reviews}
            onMediaClick={handleMediaClick}
            onReadMore={handleReadMore}
            mediaIndexMap={mediaIndexMap}
            isDesktop={isDesktop}
            propertyId={propertyId}
          />
        </div>
      )}

      {reviewCount > 0 && (
        <div className={styles.footer}>
          <button
            className={styles.footerLink}
            type="button"
            aria-label={t("propertypage.reviews.readall")}
            onClick={() => setIsPanelOpen(true)}
          >
            {t("propertypage.reviews.readall")}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 4L10 8L6 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      {isPanelOpen && (
        <ReviewsPanel
          isOpen={isPanelOpen}
          onClose={handleClosePanel}
          overallRating={overallRating}
          ratingLabel={ratingLabel}
          reviewCount={reviewCount}
          categories={categories}
          summary={summary}
          photos={photos}
          reviews={reviews}
          onMediaClick={handleMediaClick}
          scrollToReviewId={scrollToReviewId}
          mediaIndexMap={mediaIndexMap}
          history={history}
        />
      )}

      {isGalleryOpen && ownsGallery.current && (
        <Portal rootId="forPortal">
          <Modal
            hideModal={() => dispatch(setReviewMediaGalleryOpen(false))}
            size="fullScreen"
            hideCloseButton
            // Desktop too: without an entry of its own, Back popped the drawer's entry and
            // left the gallery on screen (FR-8 wants expanded view -> drawer -> page).
            isHistoryPush
            type="reviewMediaGallery"
            animation="bottomToTop"
          >
            <ReviewMediaGallery
              media={mediaArray}
              initialIndex={galleryIndex}
              isOpen={isGalleryOpen}
              onClose={() => {
                dispatch(setReviewMediaGalleryOpen(false));
              }}
              isDesktop={isDesktop}
              reviews={reviews}
              mediaIndexMap={mediaIndexMap}
              propertyName={propertyName}
              propertyId={propertyId}
              entrySource={galleryEntrySource.current}
              onActiveMediaChange={(reviewId, mediaNumber) =>
                setActiveMedia((prev) =>
                  prev?.reviewId === reviewId && prev?.mediaNumber === mediaNumber
                    ? prev
                    : { reviewId, mediaNumber },
                )
              }
            />
          </Modal>
        </Portal>
      )}
    </section>
  );
};

export default wrapperHOC(ReviewsSectionV2, {
  componentName: "ReviewsSectionV2",
  showForChina: true,
  showForWhiteLable: true,
});
